import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { suggestLocation, clearLocationSuggestions } from '../../store/slices/inventorySlice';
import { LocationRequest, LocationSuggestion } from '../../types/inventory';
import { MapPin, Package, Lightbulb, Loader2, AlertCircle } from 'lucide-react';

interface LocationSuggestionProps {
  onLocationSelected?: (location: LocationSuggestion) => void;
}

const LocationSuggestionComponent: React.FC<LocationSuggestionProps> = ({ onLocationSelected }) => {
  const dispatch = useAppDispatch();
  const { locationSuggestions, suggestionsLoading, suggestionsError } = useAppSelector((state) => state.inventory);

  const [request, setRequest] = useState<LocationRequest>({
    style_code: '',
    quantity: 1,
    size: '',
    color: '',
    item_type: 'regular'
  });

  const handleSuggestLocation = (e: React.FormEvent) => {
    e.preventDefault();
    if (request.style_code.trim() && request.quantity > 0) {
      dispatch(suggestLocation(request));
    }
  };

  const handleClearSuggestions = () => {
    dispatch(clearLocationSuggestions());
    setRequest({
      style_code: '',
      quantity: 1,
      size: '',
      color: '',
      item_type: 'regular'
    });
  };

  const handleLocationSelect = (location: LocationSuggestion) => {
    if (onLocationSelected) {
      onLocationSelected(location);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Lightbulb className="h-5 w-5 text-yellow-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Location Suggestion</h3>
        </div>
        {locationSuggestions.length > 0 && (
          <button
            onClick={handleClearSuggestions}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
      </div>

      {/* Request Form */}
      <form onSubmit={handleSuggestLocation} className="space-y-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Style Code *
            </label>
            <input
              type="text"
              placeholder="e.g. GFW23669"
              value={request.style_code}
              onChange={(e) => setRequest(prev => ({
                ...prev,
                style_code: e.target.value.toUpperCase()
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity *
            </label>
            <input
              type="number"
              min="1"
              value={request.quantity}
              onChange={(e) => setRequest(prev => ({
                ...prev,
                quantity: parseInt(e.target.value) || 1
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Size
            </label>
            <select
              value={request.size}
              onChange={(e) => setRequest(prev => ({
                ...prev,
                size: e.target.value
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Size</option>
              <option value="UK 6">UK 6</option>
              <option value="UK 8">UK 8</option>
              <option value="UK 10">UK 10</option>
              <option value="UK 12">UK 12</option>
              <option value="UK 14">UK 14</option>
              <option value="UK 16">UK 16</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <select
              value={request.color}
              onChange={(e) => setRequest(prev => ({
                ...prev,
                color: e.target.value
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Color</option>
              <option value="Black">Black</option>
              <option value="White">White</option>
              <option value="Blue">Blue</option>
              <option value="Red">Red</option>
              <option value="Green">Green</option>
              <option value="Beige">Beige</option>
              <option value="Khaki">Khaki</option>
              <option value="Wine">Wine</option>
              <option value="Navy">Navy</option>
              <option value="Grey">Grey</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Item Type
            </label>
            <select
              value={request.item_type}
              onChange={(e) => setRequest(prev => ({
                ...prev,
                item_type: e.target.value as 'regular' | 'jeans'
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="regular">Regular</option>
              <option value="jeans">Jeans</option>
            </select>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            type="submit"
            disabled={!request.style_code.trim() || request.quantity <= 0 || suggestionsLoading}
            className="inline-flex items-center px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {suggestionsLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4 mr-2" />
            )}
            Get Location Suggestions
          </button>
        </div>
      </form>

      {/* Error Display */}
      {suggestionsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{suggestionsError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {suggestionsLoading && (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="text-gray-600 mt-2">Finding optimal locations...</p>
        </div>
      )}

      {/* Suggestions Results */}
      {locationSuggestions.length > 0 && !suggestionsLoading && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-900">
            Suggested Locations ({locationSuggestions.length} options)
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {locationSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
                onClick={() => handleLocationSelect(suggestion)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div className={`p-2 rounded-lg ${
                      suggestion.type === 'bin' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>                      {suggestion.type === 'bin' ? (
                        <Package className={`h-4 w-4 ${suggestion.type === 'bin' ? 'text-blue-600' : 'text-green-600'}`} />
                      ) : (
                        <MapPin className={`h-4 w-4 ${suggestion.type === 'rack' ? 'text-green-600' : 'text-blue-600'}`} />
                      )}
                    </div>
                    <div className="ml-3">
                      <h5 className="font-medium text-gray-900">{suggestion.location}</h5>
                      <p className="text-sm text-gray-600 capitalize">{suggestion.type}</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {suggestion.available_space} available
                    </p>
                    <p className="text-xs text-gray-500">
                      {suggestion.current_capacity} current
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded p-3">
                  <p className="text-sm text-gray-700">{suggestion.reason}</p>
                </div>
                
                {/* Utilization Bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Space Utilization</span>
                    <span>
                      {suggestion.current_capacity + request.quantity} / {suggestion.current_capacity + suggestion.available_space}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{
                        width: `${((suggestion.current_capacity + request.quantity) / (suggestion.current_capacity + suggestion.available_space)) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Suggestions */}
      {locationSuggestions.length === 0 && !suggestionsLoading && request.style_code && !suggestionsError && (
        <div className="text-center py-8">
          <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Suggestions Available</h3>
          <p className="text-gray-600">
            Unable to find optimal locations for the specified criteria. Try adjusting the parameters.
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationSuggestionComponent;
